import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ProfileSection, profileChartStyles as cs } from '@/components/organisms/ProfileSection';
import { SegmentedControl } from '@/components/organisms/SegmentedControl';
import { StatTile } from '@/components/organisms/StatTile';
import { SplitLegend } from '@/components/organisms/SplitLegend';
import { LeaderboardTable, type LeaderboardColumn, type SortDirection } from '@/components/organisms/LeaderboardTable';
import { TitleCell } from '@/components/organisms/TitleCell';
import { ShareBars, ShareBarsSkeleton } from '@/components/organisms/ShareBars';
import { MiniLineChart } from '@/components/organisms/MiniLineChart';
import { MetricCell, Skeleton, SplitBar } from '@/components/primitives';
import { apiUrl, getJson } from '@/lib/api';
import { fetchCatalog, type CatalogQuarterSplit } from '@/lib/catalog';
import { fetchGenres, type GenreMeasure } from '@/lib/genres';
import { formatCount, formatShare, formatUsdCompact, prettyQuarter } from '@/lib/format';
import { usePlatformProfileContext } from './PlatformProfileLayout';
import styles from './ProfileTabs.module.css';

const STALE = 5 * 60 * 1000;

interface TopTitle {
  parrot_id: string;
  title: string;
  content_type: string;
  main_genre?: string;
  release_year?: number;
  is_exclusive?: boolean;
  revenue_contribution_usd: number;
  revenue_share?: number;
  subscribers_acquired?: number;
  subscribers_retained?: number;
}

type TitleSort = { key: string; direction: SortDirection };
const DEFAULT_SORT: TitleSort = { key: 'revenue_contribution_usd', direction: 'desc' };
const TOP_N = 10;
const TYPE_OPTS = [
  { value: '', label: 'All' },
  { value: 'show', label: 'TV Shows' },
  { value: 'movie', label: 'Movies' },
];
const GENRE_OPTS = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'subscribers', label: 'Users' },
  { value: 'titles', label: 'Titles' },
];
const TOP_GENRES = 8;

const TV = 'var(--data-1)';
const MOVIE = 'var(--data-5)';
const ACQUIRED = 'var(--metric-acquired)';
const RETAINED = 'var(--metric-retained)';

function fetchTopTitles(platform: string, region: string, contentType: string, sort: TitleSort) {
  const q = new URLSearchParams({
    platform,
    timePeriod: 'latest',
    sort: sort.key,
    dir: sort.direction,
    limit: String(TOP_N),
  });
  if (region) q.set('region', region);
  if (contentType) q.set('contentType', contentType);
  return getJson<{ data: TopTitle[] }>(apiUrl(`/leaderboard?${q}`)).then((r) => r.data);
}

const share = (part: number, whole: number) => (whole ? part / whole : 0);
const titleCase = (v?: string) => (v ? v.replace(/\b\w/g, (c) => c.toUpperCase()) : undefined);

/**
 * Catalog Insights — what the catalogue earns and keeps. Every figure here is
 * title-attributed revenue (a different universe from platform revenue, spec
 * §2), so it is shown as shares within itself and labelled as such.
 */
export function CatalogInsightsTab() {
  const { id, region } = usePlatformProfileContext();
  const regionParam = region ?? '';
  const regionScope = region ?? 'All regions';

  const [sort, setSort] = useState<TitleSort>(DEFAULT_SORT);
  const [contentType, setContentType] = useState('');
  const [genreMeasure, setGenreMeasure] = useState<GenreMeasure>('revenue');

  const catalog = useQuery({
    queryKey: ['platform-catalog', id, regionParam],
    queryFn: () => fetchCatalog({ platform: id, region: regionParam, endDate: '' }),
    staleTime: STALE,
  });
  const genres = useQuery({
    queryKey: ['platform-genres', id, regionParam],
    queryFn: () => fetchGenres({ genre: '', platform: id, region: regionParam, endDate: '' }),
    staleTime: STALE,
  });
  const titles = useQuery({
    queryKey: ['platform-top-titles', id, regionParam, contentType, sort.key, sort.direction],
    queryFn: () => fetchTopTitles(id, regionParam, contentType, sort),
    staleTime: STALE,
    // A filter change keeps the current list up (dimmed) instead of flashing skeletons.
    placeholderData: keepPreviousData,
  });

  // ── Catalogue (exclusive vs full), latest quarter ──
  const shows = catalog.data?.micro.show ?? [];
  const movies = catalog.data?.micro.movie ?? [];
  const tv = shows[shows.length - 1];
  const mv = movies[movies.length - 1];
  const quarter = catalog.data?.macro.quarter ?? genres.data?.macro.quarter;
  const scope = [regionScope, quarter ? prettyQuarter(quarter) : ''].filter(Boolean).join(' · ');

  const totalTitles = (tv?.full.titles ?? 0) + (mv?.full.titles ?? 0);
  const totalRevenue = (tv?.full.revenue ?? 0) + (mv?.full.revenue ?? 0);
  const exclusiveShare = share(
    (tv?.exclusive.revenue ?? 0) + (mv?.exclusive.revenue ?? 0),
    totalRevenue,
  );
  const tvTitleShare = share(tv?.full.titles ?? 0, totalTitles);

  // ── Genres: top N + Other, by the chosen measure ──
  const allGenres = genres.data?.macro.genres ?? [];
  const genreRows = [...allGenres].sort((a, b) => b.total[genreMeasure] - a.total[genreMeasure]);
  const topGenre = [...allGenres].sort((a, b) => b.total.revenue - a.total.revenue)[0];
  const topGenreShare = topGenre
    ? share(
        topGenre.total.revenue,
        allGenres.reduce((a, g) => a + g.total.revenue, 0),
      )
    : 0;
  const rest = genreRows.slice(TOP_GENRES);
  const sum = (rows: typeof genreRows, part: 'acquired' | 'retained' | 'total') =>
    rows.reduce((a, g) => a + g[part][genreMeasure], 0);
  // Titles have no acquired / retained split — a single-colour bar.
  const splitGenres = genreMeasure !== 'titles';
  const genreBar = (
    key: string,
    label: string,
    total: number,
    acquired: number,
    retained: number,
  ) => ({
    key,
    label,
    value: total,
    segments: splitGenres
      ? [
          { value: acquired, color: ACQUIRED },
          { value: retained, color: RETAINED },
        ]
      : undefined,
  });
  const genreBars = [
    ...genreRows
      .slice(0, TOP_GENRES)
      .map((g) =>
        genreBar(
          g.genre,
          g.label,
          g.total[genreMeasure],
          g.acquired[genreMeasure],
          g.retained[genreMeasure],
        ),
      ),
    ...(rest.length
      ? [
          genreBar(
            'other',
            `Other (${rest.length})`,
            sum(rest, 'total'),
            sum(rest, 'acquired'),
            sum(rest, 'retained'),
          ),
        ]
      : []),
  ];
  const formatGenre = (v: number) =>
    genreMeasure === 'revenue'
      ? formatUsdCompact(v)
      : genreMeasure === 'titles'
        ? v.toLocaleString()
        : formatCount(v);

  // ── Exclusive revenue share over the last 8 quarters ──
  const exclusiveTrend = (() => {
    const byQ = new Map<string, { label: string; tv?: number; movie?: number }>();
    const put = (rows: CatalogQuarterSplit[], key: 'tv' | 'movie') =>
      rows.slice(-8).forEach((r) => {
        const row = byQ.get(r.quarter) ?? { label: prettyQuarter(r.quarter) };
        row[key] = r.full.revenue ? (r.exclusive.revenue / r.full.revenue) * 100 : undefined;
        byQ.set(r.quarter, row);
      });
    put(shows, 'tv');
    put(movies, 'movie');
    return [...byQ.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  })();

  // Top titles as the Titles leaderboard draws them: same table, same cells.
  // Bars scale to each column's largest value in the list.
  const titleRows = titles.data ?? [];
  const colMax = (pick: (t: TopTitle) => number | undefined) =>
    titleRows.reduce((m, t) => Math.max(m, pick(t) ?? 0), 0) || 1;
  const maxRevenue = colMax((t) => t.revenue_contribution_usd);
  const maxShare = colMax((t) => t.revenue_share);
  const maxAcquired = colMax((t) => t.subscribers_acquired);
  const maxRetained = colMax((t) => t.subscribers_retained);
  const titleColumns: LeaderboardColumn<TopTitle>[] = [
    {
      id: 'rank',
      label: '#',
      width: '56px',
      skeleton: 'short',
      render: (_t, i) => <span className={styles.rank}>{i + 1}</span>,
    },
    {
      id: 'title',
      label: 'Title',
      skeleton: 'media',
      render: (t) => (
        <TitleCell
          parrotId={t.parrot_id}
          title={t.title}
          meta={[t.content_type === 'show' ? 'TV Show' : 'Movie', titleCase(t.main_genre), t.release_year]
            .filter(Boolean)
            .join(' • ')}
          // Exclusive is the norm for a platform's top titles; flag the exception.
          tag={t.is_exclusive === false ? 'Licensed' : undefined}
        />
      ),
    },
    {
      id: 'revenue',
      label: 'Revenue',
      width: '170px',
      sortKey: 'revenue_contribution_usd',
      sortFirst: 'desc',
      help: 'Revenue attributed to this title on this platform in the latest quarter.',
      render: (t) => (
        <MetricCell
          value={formatUsdCompact(t.revenue_contribution_usd)}
          fraction={t.revenue_contribution_usd / maxRevenue}
          tone="revenue"
        />
      ),
    },
    {
      id: 'share',
      label: 'Share',
      width: '130px',
      sortKey: 'revenue_share',
      sortFirst: 'desc',
      help: "This title's revenue as a share of the platform's title-attributed revenue for its content type.",
      render: (t) =>
        t.revenue_share != null ? (
          <MetricCell value={formatShare(t.revenue_share)} fraction={t.revenue_share / maxShare} tone="share" />
        ) : (
          <span className={styles.noValue}>—</span>
        ),
    },
    {
      id: 'acquired',
      label: 'Acquired Users',
      width: '170px',
      sortKey: 'subscribers_acquired',
      sortFirst: 'desc',
      help: 'Subscribers whose sign-up is attributed to this title.',
      render: (t) => (
        <MetricCell
          value={formatCount(t.subscribers_acquired ?? 0)}
          fraction={(t.subscribers_acquired ?? 0) / maxAcquired}
          tone="acquired"
        />
      ),
    },
    {
      id: 'retained',
      label: 'Retained Users',
      width: '170px',
      sortKey: 'subscribers_retained',
      sortFirst: 'desc',
      help: 'Existing subscribers whose continued subscription is attributed to this title.',
      render: (t) => (
        <MetricCell
          value={formatCount(t.subscribers_retained ?? 0)}
          fraction={(t.subscribers_retained ?? 0) / maxRetained}
          tone="retained"
        />
      ),
    },
  ];

  return (
    <div className={styles.tab}>
      {/* ── Headline tiles ── */}
      <div className={styles.tileRow} data-print-keep>
        <StatTile
          label="Content Catalog"
          loading={catalog.isLoading}
          value={totalTitles.toLocaleString()}
          sub={`titles · ${scope}`}
        >
          {totalTitles > 0 && (
            <>
              <SplitBar
                segments={[
                  { fraction: tvTitleShare, color: TV },
                  { fraction: 1 - tvTitleShare, color: MOVIE },
                ]}
              />
              <SplitLegend
                items={[
                  { label: `TV ${formatShare(tvTitleShare, 0)}`, color: TV },
                  { label: `Movies ${formatShare(1 - tvTitleShare, 0)}`, color: MOVIE },
                ]}
              />
            </>
          )}
        </StatTile>

        <StatTile
          label="Top Genre"
          loading={genres.isLoading}
          value={topGenre?.label ?? '—'}
          sub={topGenre ? `${formatShare(topGenreShare, 0)} of title-attributed revenue` : scope}
        >
          {topGenre && <SplitBar extent={topGenreShare} segments={[{ fraction: 1, color: TV }]} />}
        </StatTile>

        <StatTile
          label="Exclusive Share"
          loading={catalog.isLoading}
          value={totalRevenue ? formatShare(exclusiveShare, 0) : '—'}
          sub="of title-attributed revenue from exclusives"
        >
          {totalRevenue > 0 && (
            <SplitBar extent={exclusiveShare} segments={[{ fraction: 1, color: TV }]} />
          )}
        </StatTile>
      </div>

      {/* ── Top titles ── */}
      <ProfileSection
        title="Top Titles"
        scope={scope}
        hint="This platform's top 10 titles in the latest quarter. Sort by any figure from its column header. Titles are exclusive unless tagged Licensed."
        link={{
          label: 'Full leaderboard',
          to: `/titles/leaderboard?${new URLSearchParams({
            platform: id,
            ...(region ? { region } : {}),
            ...(contentType ? { contentType } : {}),
          })}`,
        }}
        controls={
          <SegmentedControl
            variant="field"
            options={TYPE_OPTS}
            value={contentType}
            defaultValue=""
            onChange={setContentType}
          />
        }
      >
        {!titles.isLoading && titleRows.length === 0 ? (
          <div className={cs.empty}>No titles in this scope</div>
        ) : (
          <div className={styles.titleTable} data-stale={titles.isPlaceholderData || undefined}>
            <LeaderboardTable
              columns={titleColumns}
              rows={titleRows}
              getRowKey={(t) => t.parrot_id}
              sort={sort}
              // Always highest first: a header picks what "top" means; flipping to
              // ascending would turn the top 10 into the bottom 10.
              onSortChange={(next) => setSort({ key: next.key, direction: 'desc' })}
              loading={titles.isLoading}
              loadingRows={TOP_N}
            />
          </div>
        )}
      </ProfileSection>

      <div className={styles.twoCol}>
        {/* ── Genre mix ── */}
        <ProfileSection
          title="Genre Mix"
          scope={scope}
          hint="Title-attributed revenue, users and titles by main genre, split into subscribers acquired (new sign-ups) and retained (kept). Titles have no acquired/retained split."
          controls={
            <SegmentedControl
              variant="field"
              options={GENRE_OPTS}
              value={genreMeasure}
              defaultValue="revenue"
              onChange={(v) => setGenreMeasure(v as GenreMeasure)}
            />
          }
        >
          {genres.isLoading ? (
            <div aria-busy="true">
              <ShareBarsSkeleton rows={TOP_GENRES + 1} />
            </div>
          ) : genreBars.length === 0 ? (
            <div className={cs.empty}>No genre data</div>
          ) : (
            <ShareBars
              rows={genreBars}
              format={formatGenre}
              showShare
              legend={
                splitGenres
                  ? [
                      { label: 'Acquired', color: ACQUIRED },
                      { label: 'Retained', color: RETAINED },
                    ]
                  : undefined
              }
            />
          )}
        </ProfileSection>

        {/* ── Exclusive vs licensed ── */}
        <ProfileSection
          title="Exclusive vs Licensed"
          scope={scope}
          hint="Exclusives stream only on this platform; licensed titles stream elsewhere too. Compares exclusives' share of titles with their share of title-attributed revenue."
        >
          {catalog.isLoading ? (
            <div aria-busy="true">
              <div className={styles.exclusiveRows}>
                {[0, 1].map((i) => (
                  <div key={i}>
                    <Skeleton shape="text" width="80%" />
                    <ShareBarsSkeleton rows={2} labelWidth="sm" />
                  </div>
                ))}
              </div>
              <div className={styles.trend}>
                <Skeleton height={180} />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.exclusiveRows}>
                {[
                  { label: 'TV Shows', row: tv, color: TV },
                  { label: 'Movies', row: mv, color: MOVIE },
                ].map(({ label, row, color }) => {
                  if (!row) return null;
                  const titleShare = share(row.exclusive.titles, row.full.titles);
                  const revenueShare = share(row.exclusive.revenue, row.full.revenue);
                  return (
                    <div key={label}>
                      <p className={styles.sentence}>
                        Exclusives are <strong>{formatShare(titleShare, 0)}</strong> of{' '}
                        {label.toLowerCase()} and earn{' '}
                        <strong>{formatShare(revenueShare, 0)}</strong> of their revenue.
                      </p>
                      <ShareBars
                        labelWidth="sm"
                        max={1}
                        color={color}
                        format={(v) => formatShare(v, 0)}
                        rows={[
                          { key: 'titles', label: 'Titles', value: titleShare },
                          { key: 'revenue', label: 'Revenue', value: revenueShare },
                        ]}
                      />
                    </div>
                  );
                })}
              </div>

              {exclusiveTrend.length > 1 && (
                <div className={styles.trend}>
                  <MiniLineChart
                    title={`Exclusive share of revenue, last ${exclusiveTrend.length} quarters`}
                    points={exclusiveTrend}
                    series={[
                      { key: 'tv', label: 'TV Shows', color: TV },
                      { key: 'movie', label: 'Movies', color: MOVIE },
                    ]}
                    format={(v) => `${v.toFixed(0)}%`}
                    domain={[0, 100]}
                  />
                </div>
              )}
            </>
          )}
        </ProfileSection>
      </div>
    </div>
  );
}
